import { useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import PropTypes from "prop-types";
import { Box } from "@mui/material";

import DataTable from "../DataTable.jsx";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { TableEntityCell, CountPill } from "../../theme/components/index.js";
import { colorsFoundation } from "../../theme/tokens/colors-foundation.js";

export default function CustomersTable({
  onRowClick = () => {},
  pageTitle,
  pageSubtitle,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();

  const tableKey = "customers";
  const storageKey = "v2_table_customers";
  const defaultRowsPerPage = getUserRowsPerPage(user, 10);

  const tablesConfig = useMemo(
    () => [
      {
        key: tableKey,
        label: "Kunden",
        route: "data/kundes",
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey,
        extraParams: {
          include: ["sachbearbeiter"],
        },
      },
    ],
    [defaultRowsPerPage, storageKey]
  );

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};
  const rows = tableState.rows || [];

  useEffect(() => {
    fetchData(tableKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      accessorKey: "display_name",
      header: "Kundenname",
      minSize: 300,
      Cell: ({ row }) => {
        const name = row.original?.display_name || "—";
        return <TableEntityCell title={name} avatar={{ label: name }} />;
      },
    },
    {
      accessorKey: "id_ov",
      header: "ID OV",
      size: 80,
    },
    {
      accessorKey: "sachbearbeiter",
      header: "Sachbearbeiter",
      Cell: ({ row }) => {
        const sb = row.original?.sachbearbeiter;
        if (!sb) {
          return null;
        }

        return sb.nachname && sb.vorname
          ? `${sb.vorname} ${sb.nachname}`
          : sb.email || sb.id_ov || "";
      },
    },
    {
      accessorKey: "vertraege_count",
      header: "Verträge",
      size: 110,
      // Sortable on the numeric count (server-side); not filterable.
      enableSorting: true,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const count = row.original?.vertraege_count ?? 0;
        return (
          <Box
            component="span"
            sx={{ fontSize: "0.8125rem", color: colorsFoundation.gray[700] }}
          >
            {`${count} ${count === 1 ? "Vertrag" : "Verträge"}`}
          </Box>
        );
      },
    },
    {
      accessorKey: "offene_schaeden_count",
      header: "Offene Schäden",
      size: 150,
      enableSorting: true,
      enableColumnFilter: false,
      Cell: ({ row }) => {
        const count = row.original?.offene_schaeden_count ?? 0;
        return (
          <CountPill
            active={count > 0}
            label={count > 0 ? `${count} offen` : "keine offen"}
          />
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      pageTitle={pageTitle}
      pageSubtitle={pageSubtitle}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) => updateTableState(tableKey, { filters })}
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      onRowClick={onRowClick}
    />
  );
}

CustomersTable.propTypes = {
  onRowClick: PropTypes.func,
  pageTitle: PropTypes.string,
  pageSubtitle: PropTypes.string,
};
